import { type ReactNode } from 'react';
import { Navigate, type RouteObject } from 'react-router';

import {
  authPages,
  GuestOnly,
  RequireAuth,
  RequirePage,
  RequirePasswordFresh,
  SessionGate,
} from '@features/auth';
import { auditPages } from '@features/audit';
import { billingPages } from '@features/billing';
import { clientsPages } from '@features/clients';
import { dashboardPages } from '@features/dashboard';
import { documentsPages } from '@features/documents';
import { vaultPages } from '@features/vault';
import { dueDatesPages } from '@features/dueDates';
import { inboxPages } from '@features/inbox';
import { myProfilePages } from '@features/myProfile';
import { servicesPages } from '@features/services';
import { usersPages } from '@features/users';
import { BaseSpinner } from '@shared/components/base';
import { NAV_ITEMS } from '@shared/constants/navigation';
import type { ConfigurablePage } from '@shared/constants/pages';

import { AppLayout } from './layouts/AppLayout';
import { NotFoundPage } from './pages/NotFoundPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { RouteErrorPage } from './pages/RouteErrorPage';

type PageModule = () => Promise<{ default: React.ComponentType }>;

const lazyPage = (load: PageModule) => async () => ({ Component: (await load()).default });

/** Built screens; every other sidebar entry shows its "Phase N" placeholder. */
const BUILT: Partial<Record<string, PageModule>> = {
  '/clients': clientsPages.list,
  '/services': servicesPages.list,
  '/due-dates': dueDatesPages.master,
  '/users': usersPages.list,
  '/audit': auditPages.list,
  '/me': myProfilePages.profile,
  '/billing': billingPages.board,
  '/inbox': inboxPages.inbox,
  '/documents': documentsPages.documents,
  '/vault': vaultPages.vault,
};

function guarded(page: string, element: ReactNode): ReactNode {
  return page === 'dashboard' || page === 'myprofile' ? (
    element
  ) : (
    <RequirePage page={page as ConfigurablePage}>{element}</RequirePage>
  );
}

const moduleRoutes: RouteObject[] = NAV_ITEMS.filter((n) => n.page !== 'dashboard').map((n) => {
  const load = BUILT[n.path];
  if (load) {
    return {
      path: n.path.slice(1),
      lazy: async () => {
        const { Component } = await lazyPage(load)();
        return { element: guarded(n.page, <Component />) };
      },
    };
  }
  return { path: `${n.path.slice(1)}/*`, element: guarded(n.page, <PlaceholderPage path={n.path} />) };
});

/** Route table (plan §14). Screens are lazy-loaded so each module ships its own chunk. */
export const routes: RouteObject[] = [
  {
    element: <SessionGate />,
    errorElement: <RouteErrorPage />,
    // Shown while the first lazy route chunk loads.
    hydrateFallbackElement: <BaseSpinner />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: 'login', lazy: lazyPage(authPages.login) },
          { path: 'forgot-password', lazy: lazyPage(authPages.forgot) },
          { path: 'reset-password', lazy: lazyPage(authPages.reset) },
        ],
      },
      { path: 'auth/callback', lazy: lazyPage(authPages.callback) },
      // Public: linked from Sign in with Google and the Google OAuth consent screen.
      { path: 'terms', lazy: lazyPage(authPages.terms) },
      { path: 'privacy', lazy: lazyPage(authPages.privacy) },
      {
        element: <RequireAuth />,
        children: [
          { path: 'change-password', lazy: lazyPage(authPages.change) },
          {
            element: <RequirePasswordFresh />,
            children: [
              { path: 'choose-year', lazy: lazyPage(authPages.chooseYear) },
              {
                path: '/',
                element: <AppLayout />,
                children: [
                  { index: true, element: <Navigate to="/dashboard" replace /> },
                  {
                    path: 'dashboard',
                    lazy: lazyPage(dashboardPages.board),
                  },
                  ...moduleRoutes,
                  {
                    path: 'documents/:tile/:id?',
                    lazy: async () => {
                      const { default: Page } = await documentsPages.documents();
                      return { element: guarded('eng', <Page />) };
                    },
                  },
                  {
                    path: 'inbox/:tab/:id?',
                    lazy: async () => {
                      const { default: Page } = await inboxPages.inbox();
                      return { element: guarded('notifs', <Page />) };
                    },
                  },
                  {
                    path: 'billing/setter/:tab?',
                    lazy: async () => {
                      const { default: Page } = await billingPages.setter();
                      return { element: guarded('billing', <Page />) };
                    },
                  },
                  {
                    path: 'services/:serviceKey',
                    lazy: async () => {
                      const { default: Page } = await servicesPages.detail();
                      return { element: guarded('services', <Page />) };
                    },
                  },
                  { path: '*', element: <NotFoundPage /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
